import { ReactNode, useEffect } from 'react';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Sider from '@/layouts/main-layout/sider';
import ResizableContactListPanel from '@/layouts/main-layout/ResizableContactListPanel';
import { PagePath } from '@/routes/paths';
import UserProfileProvider from '@/common/biz/user-profile/modal-provider';
import { useAuthUrlParams, useAuthChannel } from '@/hooks';
import { useNavEmitterListener } from '@/hooks/useNavEmitterListener';
import useUserStore from '@/stores/userStore';
import { authModalService } from '@/services/authModal';
import { useALLPushMessage } from '@/hooks/useWebSocket';
import { ModalContainer } from '@/components/moda-lInvoker';
import { Mixpanel } from '@/common/biz/mixpanel';

export default function MainLayout() {
  const location = useLocation();
  const { fetchUser } = useUserStore();

  // 应用初始化：进入页面时拉取用户信息
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // 监听 URL 参数，自动唤起认证弹窗（处理邮件链接回调等场景）
  useAuthUrlParams();

  // 监听跨 Tab 认证事件
  useAuthChannel(
    () => {
      fetchUser({ isAfterLoginSuccess: true });
      authModalService.close();
    },
    () => {
      const user = useUserStore.getState().user;
      if (!user) return;
      useUserStore.getState().cleanupAuth();
      window.location.href = '/';
    }
  );

  // 监听全局导航事件（由 navEmitter 派发，用于在非 React 上下文中触发路由跳转）
  useNavEmitterListener();

  useALLPushMessage<{ contactId: string }>(message => {
    console.log('message Test', message);
  });

  // 三栏布局：仅在 supplierDetail 路由下渲染 MessageList 中间栏
  // 回滚方案：注释下方 isSupplierDetail 相关逻辑及 <MessageList /> 即可恢复二栏
  const isSupplierDetail = useMatch(PagePath.supplierDetail);
  const isSupplier = useMatch(PagePath.supplier);
  const showContactMenu = isSupplierDetail || isSupplier;

  return (
    <div className="flex">
      <div className="h-screen relative z-10">
        <Sider className={showContactMenu ? 'mr-0!' : undefined} />
        <ModalContainer />
        <UserProfileProvider />
        <Mixpanel mixpanelId="501ba40afbebc510e14f046ca60e75f9" />
      </div>
      {/* 中间栏：仅在供应商详情路由下渲染，其余路由不渲染 */}
      {showContactMenu ? <ResizableContactListPanel /> : null}
      <div className="flex-1 h-screen overflow-hidden relative z-0">
        <AnimatePresence mode="wait">
          <motion.div
            className="relative h-full overflow-auto"
            key={location.pathname}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            // exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
export const MainContentWrapper = ({ children }: { children: ReactNode }) => {
  return <div className="h-full py-4">{children}</div>;
};
export const CustomContentWrapper = ({ children }: { children: ReactNode }) => {
  return <div className="h-full">{children}</div>;
};
